import { useEffect, useRef, useState } from "react"
import type { MailSendPreview } from "../../hooks/useAgentWebSocket"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog"

interface Props {
  open: boolean
  entity: string
  action: string
  label: string
  preview?: MailSendPreview | null
  expiresAt?: number
  busy?: boolean
  onCancel: () => void
  onConfirm: () => void
}

const TITLES: Record<string, string> = {
  delete: "Conferma eliminazione",
  send: "Conferma invio email",
}

const DESCRIPTIONS: Record<string, (label: string) => string> = {
  delete: (l) => `Sei sicuro di voler eliminare ${l}? Questa azione è irreversibile.`,
}

export function ConfirmActionDialog({
  open,
  entity: _entity,
  action,
  label,
  preview,
  expiresAt,
  busy = false,
  onCancel,
  onConfirm,
}: Props) {
  // Radix's AlertDialogCancel/Action close the dialog after running our onClick,
  // which fires onOpenChange(false). At that moment `busy` is still false (the
  // setBusy(true) from the parent hasn't committed yet), so a naive gate would
  // re-fire onCancel and emit a duplicate confirm_response: the first one is
  // consumed atomically by the backend, the second returns confirm_expired.
  // closingRef is set synchronously in the click handlers so onOpenChange can
  // distinguish a button-driven close from an Esc/click-outside dismissal.
  const closingRef = useRef(false)
  const [expired, setExpired] = useState(false)
  useEffect(() => {
    closingRef.current = false
    const remaining = expiresAt === undefined ? Infinity : expiresAt * 1000 - Date.now()
    setExpired(remaining <= 0)
    if (!Number.isFinite(remaining) || remaining <= 0) return
    const timer = setTimeout(() => setExpired(true), remaining)
    return () => clearTimeout(timer)
  }, [open, preview, expiresAt])
  const validPreview = !!preview && typeof preview.draft_id === "string" &&
    Number.isInteger(preview.version) && preview.version > 0 &&
    typeof preview.sender === "string" && !!preview.sender &&
    [preview.to, preview.cc, preview.bcc].every(items =>
      Array.isArray(items) && items.every(value => typeof value === "string")) &&
    preview.to.length + preview.cc.length + preview.bcc.length > 0 &&
    typeof preview.subject === "string" && typeof preview.body_text === "string" &&
    Array.isArray(preview.attachments) && preview.attachments.every(file =>
      file != null && typeof file.name === "string" && typeof file.sha256 === "string" &&
      Number.isFinite(file.size_bytes) && file.size_bytes >= 0)
  const sendUnavailable = action === "send" && (!validPreview || expired)


  const title = TITLES[action] ?? "Conferma"
  const desc = (DESCRIPTIONS[action] ?? ((l) => `Confermi l'operazione su ${l}?`))(label)
  const confirmText = busy ? "Attendere..." : action === "delete" ? "Elimina" : action === "send" ? "Invia" : "Conferma"

  const handleCancel = () => {
    if (closingRef.current || busy) return
    closingRef.current = true
    onCancel()
  }

  const handleConfirm = () => {
    if (closingRef.current || busy || sendUnavailable ||
      (action === "send" && expiresAt !== undefined && expiresAt * 1000 <= Date.now())) return
    closingRef.current = true
    onConfirm()
  }

  return (
    <AlertDialog
      open={open}
      onOpenChange={(o) => {
        if (!o && !busy && !closingRef.current) onCancel()
      }}
    >
      <AlertDialogContent className="bg-[var(--hexa-surface)] border-[var(--hexa-border)]">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[var(--hexa-text-primary)]">
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-[var(--hexa-text-secondary)]">
            {desc}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {preview && validPreview && action === "send" && <div className="max-h-[60vh] overflow-auto text-sm text-[var(--hexa-text-primary)]" aria-label="Anteprima email">
          <dl className="space-y-2">
            <dt>Mittente</dt><dd>{preview.sender}</dd>
            <dt>A</dt><dd>{preview.to.join(', ') || '?'}</dd>
            <dt>CC</dt><dd>{preview.cc.join(', ') || '?'}</dd>
            <dt>BCC</dt><dd>{preview.bcc.join(', ') || '?'}</dd>
            <dt>Oggetto</dt><dd>{preview.subject || 'Senza oggetto'}</dd>
            <dt>Versione</dt><dd>{preview.version}</dd>
            <dt>Corpo</dt><dd className="whitespace-pre-wrap break-words">{preview.body_text}</dd>
            <dt>Allegati</dt><dd>{preview.attachments.length ? <ul>{preview.attachments.map((file, index) => <li key={`${file.sha256}-${index}`}>{file.name} ({file.size_bytes} byte)</li>)}</ul> : 'Nessuno'}</dd>
          </dl>
        </div>}
        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={busy}
            onClick={handleCancel}
            className="bg-[var(--hexa-surface-elevated)] border-[var(--hexa-border)] text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-surface-hover)]"
          >
            Annulla
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={busy || sendUnavailable}
            className="bg-[#ef4444] text-white hover:bg-[#dc2626]"
          >
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
